/**
 * <cart-progress-bar> — tiered progress bar (free shipping, gift threshold...), computed
 * client-side from the real settings of its `cart-progress-tier` children and the real cart
 * total, never a hardcoded amount or position.
 *
 * Why this lives in JS rather than Liquid: `block.blocks` (reading a static child block's
 * settings without rendering it) returns an unresolved type ("@theme") and empty settings for
 * blocks declared inside a JSON preset — confirmed live on this store, not a theoretical concern.
 * Each `cart-progress-tier` CAN read its own settings when actually rendered though, so it writes
 * them out as data-* attributes (see blocks/cart-progress-tier.liquid); this component reads
 * those attributes back to compute the shared maximum, each tier's position (via the
 * --cart-progress-max custom property tiers already consume), and the single message to show.
 *
 * Re-renders whenever its own markup changes. Every cart update morphs this element back to the
 * server markup (Section Rendering API, or the clone of the empty-cart template): the new total
 * arrives in `data-cart-total-cents`, and everything computed here is wiped in the same pass. A
 * cart event cannot be relied on to redo it, since it resolves before the morph lands; observing
 * the element catches the morph itself, whichever path produced it.
 *
 * Once every tier is reached, the single `data-message-complete` (blocks/cart-progress.liquid)
 * replaces the naive concatenation of each tier's own "reached" message — falls back to that
 * concatenation only if the merchant leaves the setting blank.
 */
class CartProgressBar extends HTMLElement {
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    this.#render();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  // Observation is suspended while rendering: the writes below are the component's own.
  #render() {
    this.#observer.disconnect();
    this.#update();
    this.#observer.observe(this, { attributes: true, childList: true, subtree: true });
  }

  #update() {
    const tiers = Array.from(this.querySelectorAll('.cart-progress-tier'))
      .map((el) => ({
        el,
        threshold: Number(el.dataset.threshold) || 0,
        messageRemaining: el.dataset.messageRemaining || '',
        messageReached: el.dataset.messageReached || '',
      }))
      .filter((tier) => tier.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);

    if (tiers.length === 0) return;

    const totalCents = Number(this.dataset.cartTotalCents) || 0;
    const currency = this.dataset.currency || undefined;
    const locale = document.documentElement.lang || undefined;
    const money = (cents) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);

    const maxThreshold = Math.max(...tiers.map((tier) => tier.threshold));
    const totalUnits = totalCents / 100;
    let progressPct = (totalUnits / maxThreshold) * 100;
    if (progressPct > 100) progressPct = 100;
    if (totalUnits <= 0) progressPct = 2;

    this.style.setProperty('--cart-progress-max', String(maxThreshold));
    this.style.setProperty('--cart-progress-pct', `${progressPct}%`);

    const maxLabel = this.querySelector('[data-cart-progress-max-label]');
    if (maxLabel) maxLabel.textContent = money(maxThreshold * 100);

    let nextTier = null;
    for (const tier of tiers) {
      const reached = totalCents >= tier.threshold * 100;
      tier.el.classList.toggle('cart-progress-tier--reached', reached);
      if (!reached && !nextTier) nextTier = tier;
    }

    this.classList.toggle('cart-progress--complete', !nextTier);

    const messageEl = this.querySelector('[data-cart-progress-message]');
    if (!messageEl) return;

    if (!nextTier) {
      const completeMessage = this.dataset.messageComplete;
      messageEl.innerHTML = completeMessage || tiers.map((tier) => tier.messageReached).join(' + ');
    } else {
      const remainingCents = nextTier.threshold * 100 - totalCents;
      messageEl.innerHTML = nextTier.messageRemaining.replace(
        '[reste]',
        `<strong>${money(remainingCents)}</strong>`
      );
    }
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}
